import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Square, Check, RotateCcw, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SECONDS = 90;

const inputCls = (err) =>
  `bg-transparent border-b ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-0 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

const labelCls = "block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1";

const formatSeconds = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Recorder = () => {
  const openedAt = useRef(Date.now());
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [phase, setPhase] = useState("idle"); // idle | recording | recorded | sending | sent
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  useEffect(() => cleanup, []);

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    setServerError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined;
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setAudioUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(b);
        });
        setPhase("recorded");
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      recorderRef.current = rec;
      rec.start();
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setServerError("Mikrofon se nepodařilo spustit — povolte prosím v prohlížeči přístup k mikrofonu a zkuste to znovu.");
    }
  };

  const resetRecording = () => {
    setBlob(null);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setSeconds(0);
    setPhase("idle");
  };

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    if (!blob) next.audio = "Nejprve prosím nahrájte vzkaz.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setPhase("sending");
    try {
      const data = new FormData();
      data.append("audio", blob, "hlasovy-vzkaz.webm");
      data.append("name", form.name.trim());
      data.append("email", form.email.trim());
      data.append("website", form.website);
      data.append("elapsed_ms", String(Date.now() - openedAt.current));
      const res = await fetch(`${API}/voice-message`, { method: "POST", body: data });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.status === "ok" || json.status === "demo")) {
        setPhase("sent");
      } else {
        setPhase("recorded");
        setServerError(typeof json.detail === "string" ? json.detail : "Vzkaz se nepodařilo odeslat. Zkuste to prosím znovu.");
      }
    } catch {
      setPhase("recorded");
      setServerError("Vzkaz se nepodařilo odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  if (phase === "sent") {
    return (
      <div role="status" data-testid="voice-success" className="flex items-start gap-4 mt-6 border border-[#F0F0F0] p-5">
        <Check className="h-5 w-5 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
        <p className="text-sm text-[#252525] leading-relaxed">
          Děkuji! Váš vzkaz jsem přijal — přehraju si ho a ozvu se vám na uvedený e-mail.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {phase === "idle" && (
        <div className="text-center border border-dashed border-[#E5E5E5] p-8">
          <button
            type="button"
            data-testid="voice-record-button"
            onClick={startRecording}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-[#B69A58] text-[#B69A58] hover:bg-[#B69A58] hover:text-white transition-colors duration-300"
            aria-label="Začít nahrávat vzkaz"
          >
            <Mic className="h-6 w-6" strokeWidth={1.5} />
          </button>
          <p className="text-sm text-[#737373] mt-4">Klepnutím spustíte nahrávání · max. {MAX_SECONDS} sekund</p>
        </div>
      )}

      {phase === "recording" && (
        <div className="text-center border border-[#B69A58] bg-[#FAFAF8] p-8" data-testid="voice-recording">
          <button
            type="button"
            data-testid="voice-stop-button"
            onClick={stopRecording}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B69A58] text-white animate-pulse"
            aria-label="Zastavit nahrávání"
          >
            <Square className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <p className="font-display text-2xl text-[#252525] mt-4" data-testid="voice-timer">{formatSeconds(seconds)}</p>
          <p className="text-sm text-[#737373] mt-1">Nahrávám… klepnutím vzkaz ukončíte</p>
        </div>
      )}

      {(phase === "recorded" || phase === "sending") && (
        <form onSubmit={onSubmit} noValidate data-testid="voice-form" className="space-y-6">
          <div className="border border-[#F0F0F0] bg-[#FAFAF8] p-5">
            <audio controls src={audioUrl} className="w-full" data-testid="voice-preview" />
            <button
              type="button"
              data-testid="voice-rerecord-button"
              onClick={resetRecording}
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
              Nahrát znovu
            </button>
          </div>

          <div>
            <label htmlFor="voice-name" className={labelCls}>Jméno *</label>
            <input
              id="voice-name"
              data-testid="voice-name-input"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={set("name")}
              className={inputCls(errors.name)}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p role="alert" data-testid="voice-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="voice-email" className={labelCls}>E-mail *</label>
            <input
              id="voice-email"
              data-testid="voice-email-input"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
              className={inputCls(errors.email)}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p role="alert" data-testid="voice-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
          </div>

          <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
            <label htmlFor="voice-website">Webové stránky</label>
            <input
              id="voice-website"
              data-testid="voice-honeypot"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
            />
          </div>

          {serverError && <p role="alert" data-testid="voice-server-error" className="text-sm text-red-700">{serverError}</p>}
          <button
            type="submit"
            data-testid="voice-submit-button"
            disabled={phase === "sending"}
            className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
          >
            {phase === "sending" ? "Odesílám…" : "Odeslat vzkaz"}
          </button>
          <p className="text-xs text-[#737373] leading-relaxed">
            Nahrávku a údaje použiju výhradně k vyřízení vašeho vzkazu.{" "}
            <Link to="/ochrana-osobnich-udaju" data-testid="voice-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
              Informace o zpracování osobních údajů
            </Link>
          </p>
        </form>
      )}

      {phase === "idle" && serverError && (
        <p role="alert" data-testid="voice-mic-error" className="text-sm text-red-700 mt-4">{serverError}</p>
      )}
    </div>
  );
};

export const VoiceMessage = () => {
  return (
    <div data-testid="voice-message-card">
      <h3 className="hidden md:block font-display text-2xl md:text-3xl text-[#252525]">Nechte mi hlasový vzkaz</h3>
      <p className="hidden md:block text-sm md:text-base text-[#4A4A4A] leading-relaxed mt-4 max-w-md">
        Někdy je jednodušší to říct, než psát. Nahrájte mi vzkaz rovnou tady —
        přehraju si ho a ozvu se vám.
      </p>
      <Dialog>
        {/* Mobilní kompaktní panel */}
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="voice-open-mobile"
            className="md:hidden w-full flex items-center gap-3 border border-[#E5E5E5] px-4 py-3.5 text-left"
            aria-label="Nahrát hlasový vzkaz"
          >
            <span className="shrink-0 w-10 h-10 rounded-full border border-[#B69A58] bg-white text-[#B69A58] flex items-center justify-center" aria-hidden="true">
              <Mic className="h-4 w-4" strokeWidth={1.5} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-[#252525]">Raději mi necháte hlasový vzkaz?</span>
              <span className="block text-xs text-[#737373] mt-0.5">Nahrát vzkaz · max. 90 sekund</span>
            </span>
            <ChevronRight className="h-4 w-4 text-[#B69A58] ml-auto shrink-0" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </DialogTrigger>
        {/* Desktopový výrazný trigger */}
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="voice-open-button"
            className="hidden md:flex group mt-10 flex-col items-start gap-4"
            aria-label="Nahrát hlasový vzkaz"
          >
            <span className="relative inline-flex items-center justify-center">
              <span className="absolute inline-flex w-20 h-20 rounded-full bg-[#B69A58]/20 animate-ping [animation-duration:2.5s] motion-reduce:hidden" aria-hidden="true" />
              <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-full border border-[#B69A58] bg-white text-[#B69A58] group-hover:bg-[#B69A58] group-hover:text-white transition-colors duration-300">
                <Mic className="h-8 w-8" strokeWidth={1.5} />
              </span>
            </span>
            <span className="text-xs uppercase tracking-widest font-medium text-[#252525] group-hover:text-[#B69A58] transition-colors duration-300 border-b border-[#B69A58] pb-1">
              Nahrát vzkaz · max. 90 sekund
            </span>
          </button>
        </DialogTrigger>
        <DialogContent className="bg-white sm:max-w-md" data-testid="voice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#252525]">Hlasový vzkaz</DialogTitle>
            <DialogDescription className="text-sm text-[#4A4A4A]">
              Nahrajte, co potřebujete vyřešit — klidně jen pár vět. Doplňte jméno a e-mail, ať se vám mohu ozvat.
            </DialogDescription>
          </DialogHeader>
          <Recorder />
        </DialogContent>
      </Dialog>
    </div>
  );
};
